import React from "react";
import { Box } from "src/components/Box";
import {
  ArrowDataTransferHorizontalIcon,
  BankIcon,
} from "@hugeicons/core-free-icons";
import { getBanks } from "src/action/deposit";
import { ManualBankModel } from "src/pages/LeanDeposit/ManualBank";
import { useGetLeanCustomerQuery } from "src/store/bankAccounts/api";
import { useLocation } from "react-router-dom";
import { Connect } from "src/pages/LeanDeposit/tools/leanFunctions";
import { Head, Lead, Options } from "./style";

const sortBanks = (banks: any) => {
  return banks.sort((a, b) => {
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  });
};

/**
 * Every bank account on the client, for the selects that offer them.
 *
 * Exported and used by the deposit page. It used to be called from this
 * component too, on every open of the panel, and nothing here ever read the
 * result — a request per open whose answer was thrown away.
 */
export const getClientBankAccounts = async (client, setClientBankAccounts) => {
  Promise.all([getBanks()])
    .then((values) => {
      const userBanks = values[0];
      const allbanks = [
        ...(userBanks?.data?.filter((bank) => bank?.provider !== "leantech") ||
          []),
      ];

      const newData = allbanks.reduce(function (acc, curr) {
        const findIndex = acc.findIndex(function (item) {
          return item._id === curr._id;
        });

        if (findIndex === -1) {
          acc.push(curr);
        } else {
          acc[findIndex] = Object.assign({}, acc[findIndex], curr);
        }
        return acc;
      }, []);

      newData.forEach((b) => {
        /* Bank AND account, as the withdraw dialog states it. The ternary
           only ever produced the bank's name: its true branch dropped the
           IBAN and its false branch could not run, so two accounts at the
           same bank were two identical rows in the select. */
        b.label = [b.bankName, b.iban].filter(Boolean).join(" - ");
        b.value = b.bankIdentifier ? b.id : b._id;
      });
      setClientBankAccounts(sortBanks(newData));
    })
    .catch((_err) => {
      console.error("Error getting banks");
    });
};

/**
 * How to add a bank account: pick a way.
 *
 * The panel opened on two 566x376 cards centred in a half-window drawer with
 * nothing above them — no heading, no sentence, just an X and two choices, so
 * the first thing it said was the name of the first option. It says what it
 * is now, and what the choice is between.
 *
 * The automated route is switched off outside the deposit flow. That used to
 * be a div wrapped around the card carrying opacity: 0.5 and pointer-events:
 * none inline, which faded the whole thing — icon, title and paragraph — and
 * explained nothing; the card is properly disabled now and says where the
 * option is available.
 *
 * Three things also came out of here: a call to getClientBankAccounts whose
 * result nothing read, a getLeanBanks that filtered a bank list into state
 * nothing read either (its own call already commented out), and a `callback`
 * that was console.log.
 */
export const NewBankAccount = ({
  visible,
  setVisible,
  client,
}: {
  visible: { visibleDrawer: boolean; visibleBankModal: boolean };
  setVisible: React.Dispatch<
    React.SetStateAction<{ visibleDrawer: boolean; visibleBankModal: boolean }>
  >;
  client?: any;
}) => {
  const { data: leanCustomer } = useGetLeanCustomerQuery(0);
  const location = useLocation();
  const automaticAvailable = location.pathname === "/deposit";
  const tenant = process.env.REACT_APP_BANK_NAME || "";

  return (
    <>
      <Head>Add a bank account</Head>
      <Lead>
        Two ways to move money between your bank and your {tenant} balance.
      </Lead>

      <Options>
        <Box
          icon={ArrowDataTransferHorizontalIcon}
          title="Automated transfer"
          text={`Connect a UAE bank account and wire funds straight to your balance, without leaving ${tenant}.`}
          disabled={!automaticAvailable}
          reason="Available from the Deposit page"
          onClick={() => Connect(leanCustomer?.customerId, undefined)}
        />

        <Box
          icon={BankIcon}
          title="Standard transfer"
          text={`Enter the details of an account in any country, then wire funds to your ${tenant} balance from your own bank.`}
          onClick={() =>
            setVisible({
              visibleBankModal: true,
              visibleDrawer: false,
            })
          }
        />
      </Options>

      <ManualBankModel
        onCancel={() => {
          setVisible({ ...visible, visibleBankModal: false });
        }}
        key={visible.visibleBankModal.toString()}
        visible={visible.visibleBankModal}
        client={client}
      />
    </>
  );
};
